import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy } from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import Tips from '../common/Tips'
import { compile } from '../../../utils/search'

type SelectableMember = {
  id: number
  name: string
  profile_image?: string | null
  code?: string | null
  kana?: string | null
}

interface Props {
  selectedMember: SelectableMember | null
  callbackMember: (member: SelectableMember) => void
  memberList: SelectableMember[]
  placeholder?: string
  disabled?: boolean
}

const MemberProfile: FC<{ member: SelectableMember }> = ({ member }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
    <img
      alt={member.name}
      src={member.profile_image ?? `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
      style={{ width: "20px", height: "20px", borderRadius: "50%" }}
    />
    <ruby>
      {member.name}
      <rt>{member.kana}</rt>
    </ruby>
    {member.code ? ` (${member.code})` : ""}
  </span>
)

const SelectMemberModule: FC<Props> = (props) => {
  const {
    selectedMember,
    callbackMember,
    memberList,
    placeholder,
    disabled = false,
  } = props

  const [isSearchDisplay, setIsSearchDisplay] = useState(false)
  const [members, setMembers] = useState<Props['memberList']>([])
  const [searchMemberWord, setSearchMemberWord] = useState<string>("")

  const membersInit = useMemo(() => (
    memberList || []
  ), [memberList])

  const clickMember = (member_id: number) => {
    const tmpMember = members.find((v) => v.id === member_id)
    if (!tmpMember) return
    setIsSearchDisplay(false)
    callbackMember({
      id: tmpMember.id,
      name: tmpMember.name,
      profile_image: tmpMember.profile_image,
      code: tmpMember.code,
      kana: tmpMember.kana,
    })
  }

  const searchMember = (words: string) => {
    setSearchMemberWord(words)
    if (!words) {
      setMembers([...membersInit])
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const list = membersInit.filter((member) => {
      if (member === null) return false
      return compile(word).test(member.name.toLowerCase())
    })
    setMembers(sortBy(list, 'id'))
  }

  const resetMember = () => {
    setSearchMemberWord("")
    setMembers([...membersInit])
  }

  const closeSearch = () => {
    setIsSearchDisplay(false)
    resetMember()
  }

  useEffect(() => {
    setMembers(memberList || [])
  }, [memberList])

  return (
    <>
      <div className="select_member_module">
        <button
          type="button"
          className="select_member_trigger"
          onClick={() => setIsSearchDisplay(true)}
          disabled={disabled}
          style={disabled ? { cursor: 'not-allowed' } : undefined}
        >
          {selectedMember ? <MemberProfile member={selectedMember} /> : (placeholder || "ユーザーを選択")}
        </button>
        <div className={`search_box ${isSearchDisplay || 'none'}`}>
          <div className="search_detail">
            <div className="category">
              <div className="category_title">
                ユーザー
              </div>
              <div className="search_area">
                <FontAwesomeIcon
                  icon={['fas', 'search']}
                  className="icon"
                />
                <input
                  type="text"
                  placeholder="ユーザーを検索"
                  onChange={(event) => searchMember(event.target.value)}
                  value={searchMemberWord}
                />
                {
                  searchMemberWord && (
                    <button type="button" className="reset" onClick={resetMember}>
                      <FontAwesomeIcon icon={['far', 'times-circle']} />
                    </button>
                  )
                }
              </div>
              <div className="chip_list limit">
                {
                  members.map((member) => (
                    <button
                      type="button"
                      className={`chip ${selectedMember?.id === member.id ? "checked" : ""}`}
                      key={`member_${member.id}`}
                      onClick={() => clickMember(member.id)}
                    >
                      {member.name}
                    </button>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={`close_screen ${isSearchDisplay || 'none'}`} role="presentation" onClick={() => closeSearch()} />
    </>
  )
}

SelectMemberModule.defaultProps = {
  placeholder: "ユーザーを選択",
}

export default SelectMemberModule
